import React from 'react'
import { useFormikContext } from 'formik'
import { Card, Element, Form as BulmaForm, Heading, Level } from 'react-bulma-components'
import FormField from '../../../../form/fields/form-field'
import { WorkPeriodsEditor } from '../../../../work-periods/work-periods-editor/work-periods-editor'
import { useJobTitlesQuery } from '../../../../../queries/job-titles'
import { useUpdateMutation } from '../../../../../queries/workers-requests'
import { RequestButton, RequestMessage } from '../../../../request-components/request-components'
import { WorkersRequest } from '../../../../../api/workers-requests'
import { JobTitle } from '../../../../../api/job-titles'
import {
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormErrors,
} from '../workers-request-form.types'

/**
 * Props of the `<WorkPeriodsSection>` component.
 */
export interface WorkPeriodsSectionProps {
  workersRequest?: WorkersRequest
  updateWorkersRequestMutation: ReturnType<typeof useUpdateMutation>
  isSubmitDisabled?: boolean
}

/**
 * "Horaire type de la prestation" section: renders the WorkPeriodsEditor with a
 * local-only job-title simulator (used to preview labor-cost calculations).
 *
 * When editing an existing workers request, a footer with the "update" submit
 * button is rendered below the editor.
 */
const WorkPeriodsSection: React.FC<WorkPeriodsSectionProps> = ({
  workersRequest,
  updateWorkersRequestMutation,
  isSubmitDisabled,
}) => {
  const jobTitlesQuery = useJobTitlesQuery({})
  // Local state: the simulator is purely cosmetic, not part of the form values.
  const [currentJobTitle, setCurrentJobTitle] = React.useState<JobTitle | undefined>(undefined)

  const { values, initialValues, errors, setFieldValue } =
    useFormikContext<WorkersRequestCreateUpdateFormValues>()

  const isExistingWorkersRequest = workersRequest !== undefined

  // The WorkPeriodsEditor consumes the legacy errors shape where workPeriods error
  // lives at the top level. We forward `errors.missionData?.workPeriods` to match.
  const editorErrors: WorkersRequestFormErrors = {
    ...errors,
    workPeriods: (errors as WorkersRequestFormErrors).missionData?.workPeriods,
  }

  return (
    <>
      <Heading size={4}>Horaire type de la prestation</Heading>
      <Card>
        <Card.Content>
          <WorkPeriodsEditor
            workPeriods={values.missionData.workPeriods}
            initialWorkPeriods={initialValues.missionData.workPeriods}
            resource={workersRequest}
            setWorkPeriods={wPs => setFieldValue('missionData.workPeriods', wPs)}
            errors={editorErrors}
            startDate={values.meta.startDate}
            endDate={values.meta.endDate}
            jobTitle={currentJobTitle?._id.toString()}
          />
          <Level mt={5}>
            <Level.Side></Level.Side>
            <Level.Side>
              <FormField>
                <Element renderAs="span" textWeight="bold">
                  Simuler le calcul d'horaire pour
                </Element>
                <br />
                <BulmaForm.Select
                  onChange={e => {
                    setCurrentJobTitle(jobTitlesQuery.data?.find(jT => jT._id === e.target.value))
                  }}
                >
                  <option style={{ display: 'none' }} value={undefined}>
                    {'Choisir un Métier'}
                  </option>
                  {jobTitlesQuery.data?.map(jobTitle => (
                    <option key={jobTitle._id} value={jobTitle._id}>
                      {jobTitlesQuery.data?.find(jT => jobTitle._id === jT._id)?.name}
                    </option>
                  ))}
                </BulmaForm.Select>
              </FormField>
            </Level.Side>
          </Level>
        </Card.Content>
        {isExistingWorkersRequest && (
          <Card.Footer>
            <Card.Footer.Item>
              <Element>
                <Element>
                  <RequestMessage
                    mutation={updateWorkersRequestMutation}
                    displayOn={{ success: 'La demande a été mise à jour' }}
                  />
                </Element>
                <RequestButton
                  mutation={updateWorkersRequestMutation}
                  color="primary"
                  type="submit"
                  disabled={isSubmitDisabled}
                  data-test="submit-workers-request-form"
                >
                  {'Mettre à jour la Demande'}
                </RequestButton>
              </Element>
            </Card.Footer.Item>
          </Card.Footer>
        )}
      </Card>
    </>
  )
}

export default WorkPeriodsSection
